import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import React, {useEffect, useState} from "react";
import {Col, Row} from "reactstrap";
import Dialog from "../../base/dialog";
import {Divider} from "@mui/material";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import ToastService from "../../../../core/services/toast";
import useAccessLevels from "../../../hooks/use-access-levels";
import {MuiRFF, Yup} from '../../form';


const AddVendorModal = ({open, setOpen, initialValues: initialValuesProp, addVendor, onBack}) => {
    const [initialValues, setInitialValues] = useState({});
    const [loading, setLoading] = useState(false);
    const operatorAccessLevels = useAccessLevels();

    const initialSchema = Yup.object().shape({
        vendorName: Yup.string().nullable().required(ValidateMessages.required),
        vendorCode: Yup.string().nullable().required(ValidateMessages.required),
        vendorEmail: Yup.string().nullable().required(ValidateMessages.required),
        vendorPhone: Yup.string().nullable().required(ValidateMessages.required),
    });

    const validate = MuiRFF.makeValidate(initialSchema);
    const required = MuiRFF.makeRequired(initialSchema);


    /**
     * Checks if the user has access to create a new vendor. If not, it closes the modal and shows an error message.
     */
    useEffect(() => {
        if (!open) {
            return;
        }
        if (!operatorAccessLevels.packages.base.createNewVendor) {
            setOpen(false);
            ToastService.error({
                title: "Access Denied",
                message: "You do not have access to create a new vendor. Please contact your administrator.",
            });
        }
    }, [open, operatorAccessLevels.packages.base.createNewVendor, setOpen])

    /**
     * Sets the initial values of the form when the modal is opened.
     */
    useEffect(() => {
        if (!open) {
            return;
        }
        setInitialValues({
            vendorName: initialValuesProp?.name,
            vendorCode: initialValuesProp?.code,
            vendorEmail: initialValuesProp?.email,
            vendorPhone: initialValuesProp?.phone,
        });
    }, [initialValuesProp?.code, initialValuesProp?.email, initialValuesProp?.name, initialValuesProp?.phone, open])

    /**
     * Registers a new vendor into the system.
     * @param values
     */
    const onSubmit = (values) => {
        setLoading(true);
        const forApi = {
            name: values.vendorName,
            code: values.vendorCode,
            email: values.vendorEmail,
            phone: values.vendorPhone,
        }
        addVendor(forApi, (response) => {
            setLoading(false);
            if (response?.resultFlag) {
                setOpen(false);
            }
        })
    }

    /**
     * Navigates back from the modal.
     */
    const goBack = () => {
        setOpen(false);
        if (onBack) onBack();
    }

    const colClass = 'px-3 mb-4';
    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            disableBackdropClick={loading}
            disableEscapeKeyDown={loading}
        >
            <Row className='no-gutters align-items-center w-100 transition'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className='text-lg text-color-1 font-weight-semi-bold text-center'>
                        Vendor Online Profile
                    </p>
                </Row>
                <Divider className='divider'/>
                <div className='my-3 w-100'/>
                <Form
                    className='w-100'
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    render={({submitting, values, form}) => {
                        return (
                            <Row className='no-gutters justify-content-around w-100'>
                                <Row className={'no-gutters w-100 justify-content-start px-3'}>
                                    <Col xs={12} className={colClass}>
                                        <OutlinedInputForm
                                            label={'Vendor Code'}
                                            name={'vendorCode'}
                                            type={'text'}
                                            required={required.vendorCode}
                                            value={values?.vendorCode ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={12} className={colClass}>
                                        <OutlinedInputForm
                                            label={'Vendor Name'}
                                            name={'vendorName'}
                                            type={'text'}
                                            required={required.vendorName}
                                            value={values?.vendorName ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={12} className={colClass}>
                                        <OutlinedInputForm
                                            label={'Vendor Email'}
                                            name={'vendorEmail'}
                                            type={'text'}
                                            required={required.vendorEmail}
                                            value={values?.vendorEmail ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={12} className={colClass}>
                                        <OutlinedInputForm
                                            label={'Vendor Phone'}
                                            name={'vendorPhone'}
                                            type={'text'}
                                            required={required.vendorPhone}
                                            value={values?.vendorPhone ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                </Row>
                                <div className='my-1 w-100'/>
                                <Divider className='divider'/>
                                <Row className='no-gutters justify-content-between align-items-start w-100'>
                                    <Button
                                        disabled={!open || submitting || loading}
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={goBack}
                                        borderRadius={'0 8px 0 8px'}
                                    >
                                        Close
                                    </Button>
                                    <Button
                                        disabled={!open || submitting || loading}
                                        variant={'dialog'}
                                        color={'primary'}
                                        type={'submit'}
                                        borderRadius={'8px 0 8px 0'}
                                    >
                                        {!loading ? 'Register' : 'Processing...'}
                                    </Button>
                                </Row>
                            </Row>
                        );
                    }}
                />
            </Row>
        </Dialog>
    );
}

export default AddVendorModal;
